import React, { useState } from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface CopyLinkFieldProps extends Omit<BoxProps, 'onCopy'> {
  value: string
  copyLabel?: string
  copiedLabel?: string
  /** Fires after a successful copy — use for analytics; the field already writes to the clipboard itself. */
  onCopy?: (value: string) => void
}

const Root = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  height: 46,
  borderRadius: 12,
  background: 'var(--ds-surface)',
  boxShadow: 'inset 0 0 0 1px var(--ds-border)',
  padding: '0 8px 0 14px',
})

const UrlText = styled('span')({
  flex: 1,
  minWidth: 0,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 12,
  color: 'var(--ds-ink)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const CopyButton = styled('button', {
  shouldForwardProp: (p) => p !== '$copied',
})<{ $copied?: boolean }>(({ $copied }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  flexShrink: 0,
  height: 32,
  padding: '0 14px',
  borderRadius: 'var(--ds-radius-pill)',
  fontSize: 12,
  fontWeight: 650,
  cursor: 'pointer',
  transition: 'background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)',
  background: $copied ? 'var(--ds-success)' : 'var(--ds-ink)',
  color: $copied ? '#fff' : 'var(--ds-inverse-ink)',
}))

/** Labeled URL row with a one-tap copy button — shows a "Copied" state for 1.6s. */
export function CopyLinkField({
  value,
  copyLabel = 'Copy',
  copiedLabel = 'Copied',
  onCopy,
  ...rest
}: CopyLinkFieldProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    // Fire-and-forget: the "Copied" feedback shouldn't block on (or hang
    // waiting for) the clipboard permission flow — some embedded/automated
    // contexts never resolve it.
    navigator.clipboard?.writeText(value).catch(() => {
      // clipboard API unavailable or denied — feedback still shows below
    })
    onCopy?.(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }

  return (
    <Root {...rest}>
      <UrlText>{value}</UrlText>
      <CopyButton type='button' $copied={copied} onClick={handleCopy}>
        {copied ? copiedLabel : copyLabel}
      </CopyButton>
    </Root>
  )
}
